/**
 * EstateAssetsCard — dashboard estate visibility (ESTATE ownership model,
 * 2026-07-11 Paul ruling, tasks/estate-ownership-20260711/DESIGN.md, Wave H).
 *
 * Reads `GET /v1/estate/assets` (`useEstateAssets`) — the ONE tenant-wide read
 * that lists a client's assets regardless of how many trusts they have,
 * INCLUDING ZERO. Hero / RegistrationJourney / WealthDistribution all require
 * a trust in scope; this card doesn't, so it is the surface that lets an
 * estate-only client (no trusts yet, everything `person_owned`) see their own
 * assets at all.
 *
 * Deliberately shows only the `person_owned` + `earmarked_for_trust` assets —
 * the two states with NOWHERE ELSE to be seen tenant-wide. A
 * `registered_to_trust` asset already has a home on its trust's own Assets tab
 * + the wealth-distribution donut; repeating it here would just be noise.
 *
 * DEFECT B FIX (2026-07-12): the totals strip mirrors that same by-design
 * exclusion — it shows ONLY the earmarked + person-owned figures, never a
 * "registered to trusts" clause. `data.totals.registeredValueRand` is summed
 * from `AssetDoc.valueZar` — a DIFFERENT store from the hero's
 * `TrustDoc.totalValue` (a holdings/AVM revaluation moves one and not the
 * other) — so showing it here risked the same screen displaying two
 * disagreeing "registered" numbers. The backend response shape is UNCHANGED
 * (`registeredValueRand` still comes down the wire); this is purely a
 * card-rendering fix, so it deliberately does NOT reuse `estateSplitParts`
 * (which always includes the registered clause as the honest AUM anchor —
 * correct for the hero, wrong for this card).
 *
 * Honest empty state, mirroring the house idiom (`ValuesAttentionCard`):
 * renders NOTHING (not even a "your estate is empty" card) while loading, on
 * error, or when there is nothing outside a trust to show — a quiet card that
 * only appears when it has something to say.
 */

import { Icon } from '../icon';
import { formatRand, formatZarCents } from '../../lib/fmt-r';
import {
  assetStatusBadge,
  assetTypeIconName,
  assetTypeLabel,
  useEstateAssets,
} from '../../lib/api';

export function EstateAssetsCard() {
  const { data, isLoading, isError } = useEstateAssets();

  if (isLoading || isError || !data) return null;

  const outsideTrust = data.assets.filter((a) => a.ownershipState !== 'registered_to_trust');
  // Nothing outside a trust — either a genuinely empty estate, or everything
  // is already registered and visible on its trust's own Assets tab. Either
  // way, this card has nothing to add.
  if (outsideTrust.length === 0) return null;

  // DEFECT B FIX: NO registered clause — this card lists only person_owned +
  // earmarked_for_trust rows by design, so the strip shows only those two
  // figures, zero-suppressed independently (same suppression rule
  // `estateSplitParts` uses for its non-registered parts).
  //
  // PER-FIRM VISIBILITY SCOPES (2026-08-23): `totals` is ABSENT when the client
  // has withheld their portfolio value from the firm acting-as. The strip then
  // says so in words rather than rendering "R0 personal", which would be a
  // false statement about the client's estate, not a hidden one.
  const totals = data.totals;
  const parts: Array<{ key: 'earmarked' | 'personal'; label: string; valueRand: number }> = [];
  if (totals && totals.earmarkedValueRand > 0) {
    parts.push({ key: 'earmarked', label: 'transfer in progress', valueRand: totals.earmarkedValueRand });
  }
  if (totals && totals.personOwnedValueRand > 0) {
    parts.push({ key: 'personal', label: 'personal', valueRand: totals.personOwnedValueRand });
  }

  return (
    <div className="row">
    <div className="card" data-testid="estate-assets-card">
      <div className="card-head">
        <div>
          {/* Beno's walkthrough (2026-07-14/15 S27): "this site manages
              trusts and property companies, not an estate" — his reaction was
              really to defect 6 (a company-held asset showing here AND on the
              trust structure at once), but the "Your estate" framing itself
              read as a bigger claim than the card makes. Retitled to name
              what's actually listed: assets not yet moved into a trust. */}
          <div className="eyebrow">Personal assets</div>
          <h2 className="card-title">Not yet under a trust</h2>
        </div>
        <span className="card-meta" data-testid="estate-assets-split">
          {totals
            ? parts.map((p) => `${formatRand(p.valueRand)} ${p.label}`).join(' · ')
            : 'Values withheld by client'}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {outsideTrust.map((a, i) => {
          const badge = assetStatusBadge(a.ownershipState);
          return (
            <div
              key={a.id}
              data-testid={`estate-asset-row-${a.id}`}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '11px 0',
                borderBottom: i === outsideTrust.length - 1 ? 'none' : '1px solid var(--hairline)',
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                  background: 'var(--surface-2)', display: 'grid', placeItems: 'center',
                  color: 'var(--ink-2)',
                }}
              >
                <Icon name={assetTypeIconName(a.assetType)} size={15} />
              </span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {a.description}
                </div>
                <div style={{ fontSize: 12, color: 'var(--ink-4)', marginTop: 1 }}>
                  {assetTypeLabel(a.assetType)}
                </div>
              </div>
              <span
                style={{ fontSize: 13.5, color: 'var(--ink)', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}
              >
                {formatZarCents(a.valueZar)}
              </span>
              <span
                data-testid={`estate-asset-status-${a.id}`}
                style={{
                  flexShrink: 0, fontSize: 11, fontWeight: 600, padding: '3px 8px',
                  borderRadius: 999, background: badge.bg, color: badge.fg, whiteSpace: 'nowrap',
                }}
              >
                {badge.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
    </div>
  );
}
